import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { FISCAL_YEARS } from "@/constants/modules";
import { Percent, ArrowLeft, Upload, Loader2, FileJson, AlertCircle, CheckCircle2, Globe } from "lucide-react";

const SAMPLE_JSON = `{
  "year": 2025,
  "jurisdiction": "federal",
  "brackets": [
    {"min": 0, "max": 55867, "rate": 0.15},
    {"min": 55867, "max": 111733, "rate": 0.205},
    {"min": 111733, "max": 173205, "rate": 0.26},
    {"min": 173205, "max": 246752, "rate": 0.29},
    {"min": 246752, "max": null, "rate": 0.33}
  ],
  "description": "Federal Canada 2025"
}`;

/**
 * Iter92 — Taux d'imposition.
 *
 * Visualisation par annee + import JSON (replace complet si annee existe).
 */
export default function TaxRatesPage() {
  const navigate = useNavigate();
  const [years, setYears] = useState([]); // [{year, jurisdiction, brackets_count, imported_at}]
  const [year, setYear] = useState(2025);
  const [jurisdiction, setJurisdiction] = useState("federal");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Import state
  const [showImport, setShowImport] = useState(false);
  const [jsonText, setJsonText] = useState(SAMPLE_JSON);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState("");

  const loadList = useCallback(async () => {
    try {
      const r = await api.get("/reports/tax-rates/list");
      setYears(r.data?.items || []);
    } catch {
      setYears([]);
    }
  }, []);

  const loadYear = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/tax-rates", { params: { year, jurisdiction } });
      setData(r.data);
    } catch {
      setData({ year, jurisdiction, brackets: [], missing: true });
    } finally {
      setLoading(false);
    }
  }, [year, jurisdiction]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadYear(); }, [loadYear]);

  const handleImport = async () => {
    if (importing) return;
    setParseError("");
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setParseError("JSON invalide : " + e.message);
      return;
    }
    if (!parsed.year || !Array.isArray(parsed.brackets)) {
      setParseError("Format requis : {year: int, brackets: [{min, max, rate}]}");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/reports/tax-rates/import", parsed);
      toast.success(`Import OK : ${r.data.brackets_count} paliers ${r.data.replaced ? "(remplaces)" : "(crees)"}`);
      setShowImport(false);
      await loadList();
      // Switch to imported year/jurisdiction for visualisation
      setYear(parsed.year);
      setJurisdiction(parsed.jurisdiction || "federal");
    } catch (err) {
      const msg = err.response?.data?.detail;
      setParseError(typeof msg === "string" ? msg : JSON.stringify(msg) || "Erreur");
    } finally {
      setImporting(false);
    }
  };

  const fmtPercent = (r) => `${(r * 100).toFixed(2)}%`;
  const fmtCurrency = (n) => n === null || n === undefined
    ? "Au-dela"
    : n.toLocaleString("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });

  return (
    <div className="max-w-5xl mx-auto" data-testid="tax-rates-page">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button
          onClick={() => navigate("/admin/fiscal")}
          className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition active:scale-90"
          aria-label="Retour"
          data-testid="tax-rates-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <Percent className="w-5 h-5 text-fuchsia-600" />
        <h1 className="text-2xl font-bold text-gray-900">Taux d'imposition</h1>
      </div>

      {/* Filtres */}
      <div className="bg-white border rounded-xl p-4 mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-gray-500 mr-1">Annee :</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border rounded-lg px-3 py-1.5 text-sm"
            data-testid="tax-rates-year"
          >
            {FISCAL_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            {/* Inclut aussi les annees deja importees (au cas ou) */}
            {years.filter((y) => !FISCAL_YEARS.includes(y.year)).map((y) => (
              <option key={`extra-${y.year}-${y.jurisdiction}`} value={y.year}>{y.year} ({y.jurisdiction})</option>
            ))}
          </select>
          <label className="text-xs text-gray-500 ml-2 mr-1">Juridiction :</label>
          <select
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm"
            data-testid="tax-rates-jurisdiction"
          >
            <option value="federal">Federal (Canada)</option>
            <option value="provincial">Provincial</option>
            <option value="quebec">Quebec</option>
          </select>
        </div>
        <button
          onClick={() => setShowImport((v) => !v)}
          className="bg-fuchsia-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-fuchsia-700 flex items-center gap-2"
          data-testid="tax-rates-import-toggle"
        >
          <Upload className="w-4 h-4" />
          {showImport ? "Fermer import" : "Importer JSON"}
        </button>
      </div>

      {/* Import form */}
      {showImport && (
        <div className="bg-white border-2 border-dashed border-fuchsia-300 rounded-xl p-5 mb-4 space-y-3" data-testid="tax-rates-import-form">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <FileJson className="w-4 h-4 text-fuchsia-600" /> Coller le JSON
          </h3>
          <p className="text-xs text-gray-500">
            Format : {`{ year: int, jurisdiction: "federal", brackets: [{min, max, rate}] }`}.
            Si l'annee+juridiction existe, les paliers seront <strong>remplaces</strong>.
          </p>
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            className="w-full border rounded-lg p-3 text-xs font-mono"
            rows={12}
            data-testid="tax-rates-import-textarea"
          />
          {parseError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg flex items-start gap-2" data-testid="tax-rates-import-error">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span className="whitespace-pre-wrap">{parseError}</span>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleImport}
              disabled={importing}
              className="bg-fuchsia-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-fuchsia-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="tax-rates-import-submit"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importing ? "Import en cours..." : "Importer"}
            </button>
            <button
              onClick={() => setJsonText(SAMPLE_JSON)}
              className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
              data-testid="tax-rates-import-reset"
            >
              Charger l'exemple
            </button>
          </div>
        </div>
      )}

      {/* Tableau des paliers */}
      <div className="bg-white border rounded-xl overflow-hidden" data-testid="tax-rates-table-card">
        <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm">
            <Globe className="w-4 h-4 text-gray-500" />
            <span className="font-semibold">{year}</span>
            <span className="text-gray-400">·</span>
            <span className="capitalize">{jurisdiction}</span>
            {data && !data.missing && data.description && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-xs text-gray-500 italic truncate">{data.description}</span>
              </>
            )}
          </div>
          {data && !data.missing && (
            <span className="text-[10px] text-gray-400">
              Importe le {data.imported_at ? new Date(data.imported_at).toLocaleDateString("fr-CA") : "—"}
            </span>
          )}
        </div>
        {loading ? (
          <div className="p-10 text-center text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : data?.missing ? (
          <div className="p-10 text-center text-gray-400 text-sm" data-testid="tax-rates-empty">
            Aucun palier importe pour {year} ({jurisdiction}).
            <br />
            Cliquez sur <strong>Importer JSON</strong> pour ajouter une table.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="tax-rates-table">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Tranche</th>
                <th className="text-right px-4 py-2">Min</th>
                <th className="text-right px-4 py-2">Max</th>
                <th className="text-right px-4 py-2">Taux</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(data?.brackets || []).map((b, i) => (
                <tr key={i} className="hover:bg-gray-50" data-testid={`tax-rate-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-xs">#{i + 1}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtCurrency(b.min)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtCurrency(b.max)}</td>
                  <td className="px-4 py-2 text-right font-bold text-fuchsia-700 tabular-nums">{fmtPercent(b.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Historique des annees importees */}
      {years.length > 0 && (
        <div className="bg-white border rounded-xl mt-4 overflow-hidden">
          <div className="px-4 py-2 border-b bg-gray-50 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Tables importees ({years.length})
          </div>
          <div className="divide-y">
            {years.map((y) => (
              <button
                key={`${y.year}-${y.jurisdiction}`}
                onClick={() => { setYear(y.year); setJurisdiction(y.jurisdiction); }}
                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 transition ${
                  y.year === year && y.jurisdiction === jurisdiction ? "bg-fuchsia-50" : ""
                }`}
                data-testid={`tax-rates-history-${y.year}-${y.jurisdiction}`}
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-fuchsia-600 flex-shrink-0" />
                <span className="text-sm font-semibold">{y.year}</span>
                <span className="text-xs text-gray-500 capitalize">{y.jurisdiction}</span>
                <span className="text-[10px] text-gray-400 ml-auto">
                  {y.brackets_count} paliers
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
